import React, { useState } from "react";
import { Phone, EnvelopeSimple, MapPin, WhatsappLogo, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import api, { formatApiErrorDetail, CONTACT } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", city: "Akola", electricity_bill: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !/^[6-9]\d{9}$/.test(form.phone)) {
      toast.error("Enter valid name and 10-digit mobile");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: "contact" });
      setDone(true);
      toast.success("Message sent! We'll call you soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO page="contact" titleFallback="Contact Orax Solar"/>

      <section className="pt-32 pb-10 bg-[#F9F8F6]">
        <div className="container-custom">
          <div className="overline mb-3">GET IN TOUCH</div>
          <h1 className="h1 max-w-3xl">Let's talk solar</h1>
          <p className="text-lg text-[#2D4739] mt-4 max-w-2xl">
            Visit our office in Akola, call, WhatsApp, or send us a message. Our solar experts respond within 30 minutes.
          </p>
        </div>
      </section>

      <section className="pb-24 bg-[#F9F8F6]">
        <div className="container-custom grid md:grid-cols-12 gap-6">
          {/* Contact cards */}
          <div className="md:col-span-5 space-y-4">
            <a href={`tel:${CONTACT.phonePrimary}`} className="block bg-white rounded-2xl p-6 border border-[#E5E3DB] card-lift" data-testid="contact-phone">
              <div className="w-12 h-12 rounded-xl bg-[#135934]/10 text-[#135934] flex items-center justify-center mb-3">
                <Phone weight="duotone" size={24}/>
              </div>
              <div className="text-sm font-bold uppercase tracking-wider text-[#135934] mb-1">CALL US</div>
              <div className="font-display font-extrabold text-2xl text-[#0A1F15]">+91 {CONTACT.phonePrimary}</div>
              <div className="text-[#5C7368] mt-0.5">+91 {CONTACT.phoneSecondary}</div>
            </a>

            <a href={`https://wa.me/91${CONTACT.whatsapp}?text=Hi%20Orax%20Solar`} target="_blank" rel="noreferrer" className="block bg-white rounded-2xl p-6 border border-[#E5E3DB] card-lift" data-testid="contact-whatsapp">
              <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 text-[#25D366] flex items-center justify-center mb-3">
                <WhatsappLogo weight="duotone" size={24}/>
              </div>
              <div className="text-sm font-bold uppercase tracking-wider text-[#25D366] mb-1">WHATSAPP</div>
              <div className="font-display font-extrabold text-2xl text-[#0A1F15]">Chat with us</div>
              <div className="text-[#5C7368] mt-0.5">Instant replies · 9am–8pm</div>
            </a>

            <a href={`mailto:${CONTACT.email}`} className="block bg-white rounded-2xl p-6 border border-[#E5E3DB] card-lift" data-testid="contact-email">
              <div className="w-12 h-12 rounded-xl bg-[#0F52BA]/10 text-[#0F52BA] flex items-center justify-center mb-3">
                <EnvelopeSimple weight="duotone" size={24}/>
              </div>
              <div className="text-sm font-bold uppercase tracking-wider text-[#0F52BA] mb-1">EMAIL</div>
              <div className="font-display font-extrabold text-2xl text-[#0A1F15] break-all">{CONTACT.email}</div>
            </a>

            <div className="bg-white rounded-2xl p-6 border border-[#E5E3DB]">
              <div className="w-12 h-12 rounded-xl bg-[#FDB813]/20 text-[#0A1F15] flex items-center justify-center mb-3">
                <MapPin weight="duotone" size={24}/>
              </div>
              <div className="text-sm font-bold uppercase tracking-wider text-[#135934] mb-1">OFFICE</div>
              <div className="font-semibold text-[#0A1F15]">Orax Solar</div>
              <div className="text-[#5C7368] mt-1">{CONTACT.address}</div>
              <div className="flex items-center gap-1 text-xs text-[#5C7368] mt-3">
                <Clock size={12}/> Mon–Sat: 9:00 AM – 7:00 PM
              </div>
            </div>
          </div>

          {/* Form & Map */}
          <div className="md:col-span-7 space-y-4">
            <div className="bg-white rounded-2xl p-7 border border-[#E5E3DB]">
              <h2 className="h3 mb-1">Send us a message</h2>
              <p className="text-sm text-[#5C7368] mb-5">Tell us about your solar needs and we'll reach out.</p>
              {done ? (
                <div className="bg-[#135934]/10 rounded-2xl p-8 text-center">
                  <div className="h3 text-[#135934] mb-2">Thanks for reaching out!</div>
                  <p className="text-[#2D4739]">We'll call you back within 30 minutes.</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-3" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input required placeholder="Your Name *" value={form.name} onChange={e => setForm({...form, name:e.target.value})} className="input-solar w-full px-4 py-3 rounded-xl border border-[#E5E3DB]" data-testid="contact-name"/>
                    <input required type="tel" maxLength={10} placeholder="Mobile *" value={form.phone} onChange={e => setForm({...form, phone:e.target.value})} className="input-solar w-full px-4 py-3 rounded-xl border border-[#E5E3DB]" data-testid="contact-phone-input"/>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input placeholder="City" value={form.city} onChange={e => setForm({...form, city:e.target.value})} className="input-solar w-full px-4 py-3 rounded-xl border border-[#E5E3DB]"/>
                    <input placeholder="Monthly Bill (₹)" value={form.electricity_bill} onChange={e => setForm({...form, electricity_bill:e.target.value})} className="input-solar w-full px-4 py-3 rounded-xl border border-[#E5E3DB]"/>
                  </div>
                  <textarea rows={4} placeholder="Your message (optional)" value={form.message} onChange={e => setForm({...form, message:e.target.value})} className="input-solar w-full px-4 py-3 rounded-xl border border-[#E5E3DB] resize-none" data-testid="contact-message"/>
                  <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="contact-submit">
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>

            {/* Google Map */}
            <div className="rounded-2xl overflow-hidden border border-[#E5E3DB] aspect-[16/9]">
              <iframe
                title="Orax Solar Akola Location"
                src="https://www.google.com/maps?q=Tower+Chowk+Umri+Road+Akola+Maharashtra&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
